import { useRef, useState, useEffect, useCallback } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from "framer-motion";

import p1 from "../../assets/portfolio/IMG_2382.jpg";
import p2 from "../../assets/portfolio/13d96cb3-c8ed-408c-8888-4b846a45b047.jpg";
import p3 from "../../assets/portfolio/16027c31-a477-45ce-9311-d51dad05b318.jpg";
import p4 from "../../assets/portfolio/C59A4286bsmall.jpg";
import p5 from "../../assets/portfolio/IMG_2386.jpg";
import p6 from "../../assets/portfolio/IMG_2373.jpg";
import p7 from "../../assets/portfolio/IMG_2397.jpg";
import p8 from "../../assets/portfolio/IMG_9121.jpg";
import p9 from "../../assets/portfolio/IMG_6174.jpg";
import p10 from "../../assets/portfolio/IMG_1673.JPG";
import p11 from "../../assets/portfolio/014c6843-654f-469b-8f70-cbdc28258bea.jpg";
import p12 from "../../assets/portfolio/e2ccd50a-3817-4ab1-a70a-f0419e19aeea.jpg";
import p13 from "../../assets/portfolio/C59A1007b.jpg";
import p14 from "../../assets/portfolio/IMG_9436.jpg";
import { Link } from "react-router-dom";

// Strip A — top row (moves left as you scroll down)
const STRIP_A = [p13, p1, p2, p3, p4, p5, p6];
// Strip B — bottom row (moves right as you scroll down)
const STRIP_B = [p7, p8, p9, p10, p11, p12, p14];

// All photos flat for lightbox navigation
const ALL = [p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12];

// ─── Global ────────────────────────────────────────────────────────────
const LockScroll = createGlobalStyle`
  body { overflow: ${({ $locked }) => ($locked ? "hidden" : "auto")}; }
`;

// ─── Styled ────────────────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--ink);
  padding: 10rem 0 12rem;
  overflow: hidden;
`;

const TopRule = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 7vw;
  right: 7vw;
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--terracotta),
    var(--gold) 40%,
    transparent
  );
  opacity: 0.2;
  transform-origin: left;
`;

// Subtle grain on dark bg
const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
`;

// ── Header ─────────────────────────────────────────────────────────────
const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 7vw;
  margin-bottom: 6rem;
  gap: 2rem;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.2rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 32px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;
const HeadingWrap = styled.div`
  overflow: hidden;
  margin-bottom: 0.1rem;
`;
const Heading = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.8rem, 4.5vw, 4.5rem);
  font-weight: 300;
  /* line-height: 0.92; */
  color: var(--cream);
  letter-spacing: -0.01em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--warm-tan);
`;
const HeaderRight = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.78rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.8;
  color: rgba(247, 243, 238, 0.35);
  max-width: 28ch;
  text-align: right;
`;

// ── Strips ─────────────────────────────────────────────────────────────
const StripsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
`;

// The moving rail for each strip
const Rail = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 12px;
  will-change: transform;
  /* Wide enough to contain all photos + overflow both sides */
  width: max-content;
`;

// Individual photo frame — all same height, auto width
const Frame = styled(motion.div)`
  position: relative;
  height: auto;
  width: clamp(200px, 20vw, 360px);
  aspect-ratio: ${({ $portrait }) => ($portrait ? "3/4" : "4/3")};
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  background: var(--ink);
  @media (max-width: 600px) {
    width: clamp(70px, 7vw, 100px);
  }
`;

const FrameImg = styled(motion.div)`
  position: absolute;
  inset: -8% 0;
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center;
  will-change: transform;
`;

// Hover overlay — just a subtle darkening, no text
const FrameShade = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(14, 7, 4, 0.35);
  opacity: 0;
`;

// Tiny expand hint on hover
const ExpandHint = styled(motion.div)`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  z-index: 3;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(247, 243, 238, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
`;

// ── Bottom strip ───────────────────────────────────────────────────────
const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3.5rem 7vw 0;
  margin-top: 1rem;
  border-top: 1px solid rgba(247, 243, 238, 0.07);
`;

const BottomLabel = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.58rem;
  font-weight: 300;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.2);
`;

const BottomBtn = styled(motion.button)`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(247, 243, 238, 0.6);
  background: none;
  border: 1px solid rgba(247, 243, 238, 0.15);
  padding: 0.8rem 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  transition:
    border-color 0.3s,
    color 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--cream);
  }
  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(4px);
  }
`;

// ─── Lightbox ──────────────────────────────────────────────────────────
const LBBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(14, 7, 4, 0.97);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
`;

const LBImg = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: min(56vh, 82vw);
  height: min(82vh, 110vw);
  max-width: 560px;
  max-height: 82vh;
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center;
  cursor: default;
  z-index: 9001;
`;

const LBClose = styled(motion.button)`
  position: fixed;
  top: 1.8rem;
  right: 2rem;
  z-index: 9002;
  background: none;
  border: 1px solid rgba(247, 243, 238, 0.18);
  color: rgba(247, 243, 238, 0.55);
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--cream);
  }
`;

const LBArrow = styled(motion.button)`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${({ $left }) => ($left ? "left: 2rem;" : "right: 2rem;")}
  z-index: 9002;
  background: none;
  border: 1px solid rgba(247, 243, 238, 0.12);
  color: rgba(247, 243, 238, 0.4);
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--cream);
  }
  &:disabled {
    opacity: 0.1;
    pointer-events: none;
  }
`;

const LBCount = styled(motion.div)`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  font-family: var(--font-serif);
  font-size: 0.85rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(247, 243, 238, 0.25);
  z-index: 9002;
  letter-spacing: 0.1em;
`;

// ─── Single frame ──────────────────────────────────────────────────────
function PhotoFrame({ img, globalIndex, onOpen, portrait }) {
  return (
    <Frame
      $portrait={portrait}
      onClick={() => onOpen(globalIndex)}
      whileHover="hover"
    >
      <FrameImg
        $img={img}
        variants={{
          hover: {
            scale: 1.07,
            transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
          },
        }}
      />
      <FrameShade
        variants={{ hover: { opacity: 1, transition: { duration: 0.3 } } }}
      />
      <ExpandHint
        variants={{ hover: { opacity: 1, transition: { duration: 0.3 } } }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M1 1h3M1 1v3M9 9H6M9 9V6M9 1H6M9 1v3M1 9h3M1 9V6"
            stroke="rgba(247,243,238,0.7)"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </ExpandHint>
    </Frame>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────
export default function PortfolioSection() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: "-10%" });

  // Scroll progress tied to the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Strip A moves LEFT as you scroll down (-direction)
  const rawA = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);
  // Strip B moves RIGHT as you scroll down (+direction)
  const rawB = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const xA = useSpring(rawA, { stiffness: 40, damping: 16, mass: 1 });
  const xB = useSpring(rawB, { stiffness: 40, damping: 16, mass: 1 });

  // Strips fade + slide up on entrance
  const stripAInView = useInView(sectionRef, { once: true, margin: "-5%" });

  // Lightbox
  const [lbIndex, setLbIndex] = useState(null);
  const openLb = useCallback((i) => setLbIndex(i), []);
  const closeLb = useCallback(() => setLbIndex(null), []);

  useEffect(() => {
    if (lbIndex === null) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLb();
      if (e.key === "ArrowRight")
        setLbIndex((i) => Math.min(i + 1, ALL.length - 1));
      if (e.key === "ArrowLeft") setLbIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lbIndex, closeLb]);

  const revealUp = {
    hidden: { y: "105%" },
    show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
  };

  // Alternate portrait/landscape for visual rhythm in each strip
  const portraitA = [true, false, true, false, true, false];
  const portraitB = [false, true, false, true, false, true];

  return (
    <>
      <LockScroll $locked={lbIndex !== null} />

      <Section ref={sectionRef}>
        <Grain />
        <TopRule
          initial={{ scaleX: 0 }}
          animate={headerInView ? { scaleX: 1 } : {}}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />

        <Inner>
          {/* ── Header ── */}
          <Header ref={headerRef}>
            <div>
              <EyebrowRow>
                <EyebrowDash
                  initial={{ scaleX: 0 }}
                  animate={headerInView ? { scaleX: 1 } : {}}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  style={{ transformOrigin: "left" }}
                />
                <Eyebrow
                  initial={{ opacity: 0, x: -8 }}
                  animate={headerInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  My portfolio
                </Eyebrow>
              </EyebrowRow>
              <HeadingWrap>
                <Heading
                  variants={revealUp}
                  initial="hidden"
                  animate={headerInView ? "show" : "hidden"}
                  transition={{ delay: 0.25 }}
                >
                  Selected
                </Heading>
              </HeadingWrap>
              <HeadingWrap>
                <HeadingItalic
                  as={motion.h2}
                  variants={revealUp}
                  initial="hidden"
                  animate={headerInView ? "show" : "hidden"}
                  transition={{ delay: 0.37 }}
                >
                  Work
                </HeadingItalic>
              </HeadingWrap>
            </div>

            <HeaderRight
              initial={{ opacity: 0, y: 12 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.55, duration: 0.8 }}
            >
              Bridal · Editorial · Personal · Glam
            </HeaderRight>
          </Header>

          {/* ── Two filmstrips ── */}
          <StripsWrap>
            {/* Strip A — slides LEFT on scroll */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={stripAInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Rail style={{ x: xA }}>
                {STRIP_A.map((img, i) => (
                  <PhotoFrame
                    key={i}
                    img={img}
                    globalIndex={i}
                    onOpen={openLb}
                    portrait={portraitA[i]}
                  />
                ))}
              </Rail>
            </motion.div>

            {/* Strip B — slides RIGHT on scroll */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={stripAInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 1,
                delay: 0.22,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <Rail style={{ x: xB }}>
                {STRIP_B.map((img, i) => (
                  <PhotoFrame
                    key={i}
                    img={img}
                    globalIndex={i + 6}
                    onOpen={openLb}
                    portrait={portraitB[i]}
                  />
                ))}
              </Rail>
            </motion.div>
          </StripsWrap>

          {/* ── Bottom ── */}
          <Bottom>
            <BottomLabel
              initial={{ opacity: 0 }}
              animate={headerInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              12 works · 2020 — 2026
            </BottomLabel>

            <BottomBtn
              initial={{ opacity: 0, y: 8 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.7 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to={"/portfolio"} className="link-styles">
                Full portfolio
              </Link>
              <svg width="16" height="8" viewBox="0 0 16 8" fill="none">
                <path
                  d="M1 4h14M11 1l4 3-4 3"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </BottomBtn>
          </Bottom>
        </Inner>

        {/* ── Lightbox ── */}
        <AnimatePresence>
          {lbIndex !== null && (
            <>
              <LBBackdrop
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={closeLb}
              />

              <AnimatePresence mode="wait">
                <LBImg
                  key={lbIndex}
                  $img={ALL[lbIndex]}
                  initial={{ opacity: 0, scale: 0.93 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                  onClick={(e) => e.stopPropagation()}
                />
              </AnimatePresence>

              <LBClose
                onClick={closeLb}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileHover={{ scale: 1.1 }}
              >
                ✕
              </LBClose>

              <LBArrow
                $left
                disabled={lbIndex === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setLbIndex((i) => i - 1);
                }}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
              >
                <svg width="13" height="9" viewBox="0 0 13 9" fill="none">
                  <path
                    d="M12 4.5H1M5 1L1 4.5 5 8"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </LBArrow>

              <LBArrow
                disabled={lbIndex === ALL.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  setLbIndex((i) => i + 1);
                }}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
              >
                <svg width="13" height="9" viewBox="0 0 13 9" fill="none">
                  <path
                    d="M1 4.5h11M8 1l4 3.5L8 8"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </LBArrow>

              <LBCount
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {String(lbIndex + 1).padStart(2, "0")} /{" "}
                {String(ALL.length).padStart(2, "0")}
              </LBCount>
            </>
          )}
        </AnimatePresence>
      </Section>
    </>
  );
}
